import axios from 'axios';
import { getLocalHubCredential } from './hubCredentialStore';
import { pairWithHub } from './hubPairingService';
import { rehearsalOnly } from '@/services/operations/operationalCommandRouter';

let scopedSession = null;

function errorCode(error) {
  return error?.response?.data?.detail?.code
    || error?.response?.data?.detail
    || error?.message
    || 'LOCAL_REHEARSAL_ERROR';
}

async function credentialFor(store, discovery, refreshGrant = false) {
  let credential = await getLocalHubCredential(discovery?.profile?.hub_uuid);
  if (!credential || refreshGrant) {
    credential = await pairWithHub(
      store,
      discovery,
      store.state.device.device?.name || 'Dispositivo EWU',
    );
  }
  return credential;
}

async function sessionFor(store, discovery, scopes, refreshGrant = false) {
  const key = `${discovery?.profile?.hub_uuid}:${[...scopes].sort().join(',')}`;
  if (
    scopedSession?.key === key
    && scopedSession.expiresAt > Date.now() + 5000
  ) return scopedSession.token;
  const credential = await credentialFor(store, discovery, refreshGrant);
  const response = await axios.post(
    `${discovery.url}/local/v1/sessions`,
    {
      local_device_id: credential.local_device_id,
      local_device_secret: credential.local_device_secret,
      scopes,
    },
    { timeout: 5000, withCredentials: false },
  );
  scopedSession = {
    key,
    token: response.data.session_token,
    expiresAt: new Date(response.data.expires_at).getTime(),
  };
  return scopedSession.token;
}

async function request(store, discovery, scopes, config, refreshGrant = false) {
  const token = await sessionFor(store, discovery, scopes, refreshGrant);
  try {
    return await axios({
      timeout: 8000,
      withCredentials: false,
      ...config,
      url: `${discovery.url}${config.url}`,
      headers: {
        ...(config.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (error) {
    if (
      error?.response?.status === 403
      && ['LOCAL_SESSION_INVALID', 'LOCAL_SESSION_SCOPE_DENIED'].includes(
      errorCode(error),
      )
    ) {
      scopedSession = null;
      const renewed = await sessionFor(
        store,
        discovery,
        scopes,
        errorCode(error) === 'LOCAL_SESSION_SCOPE_DENIED',
      );
      return axios({
        timeout: 8000,
        withCredentials: false,
        ...config,
        url: `${discovery.url}${config.url}`,
        headers: {
          ...(config.headers || {}),
          Authorization: `Bearer ${renewed}`,
        },
      });
    }
    throw error;
  }
}

export async function createRehearsal(store, discovery, ttlSeconds = 7200) {
  // Refreshing the signed grant is deliberate: it picks up the current
  // primary/Partner permission before a privileged session is created.
  scopedSession = null;
  const response = await request(
    store,
    discovery,
    ['local:write', 'rehearsal:manage'],
    {
      method: 'post',
      url: '/local/v1/operational-sessions',
      data: { ttl_seconds: ttlSeconds },
    },
    true,
  );
  return response.data;
}

export async function transitionRehearsal(
  store, discovery, sessionUuid, transition,
) {
  const response = await request(
    store,
    discovery,
    ['local:write', 'rehearsal:manage'],
    {
      method: 'post',
      url: `/local/v1/operational-sessions/${sessionUuid}/${transition}`,
      data: {},
    },
  );
  return response.data;
}

export async function getRehearsal(store, discovery, sessionUuid) {
  const response = await request(
    store,
    discovery,
    ['local:write'],
    {
      method: 'get',
      url: `/local/v1/operational-sessions/${sessionUuid}`,
    },
  );
  return response.data;
}

export async function getCurrentRehearsal(store, discovery) {
  const response = await request(
    store,
    discovery,
    ['local:write'],
    {
      method: 'get',
      url: '/local/v1/operational-sessions/current',
    },
  );
  return response.data.session || null;
}

export async function readRehearsal(
  store, discovery, sessionUuid, contract = 'pos/bootstrap',
) {
  const response = await request(
    store,
    discovery,
    ['local:write'],
    {
      method: 'get',
      url: `/local/v1/rehearsal/read/${contract}`,
      params: { session_uuid: sessionUuid },
    },
  );
  return response.data;
}

export async function recoverRehearsalEvents(
  store, discovery, sessionUuid, afterSequence = 0,
) {
  const response = await request(
    store,
    discovery,
    ['local:write'],
    {
      method: 'get',
      url: '/local/v1/events',
      params: {
        session_uuid: sessionUuid,
        after_sequence: afterSequence,
      },
    },
  );
  return response.data;
}

export async function sendRehearsalCommand(
  store, discovery, command,
) {
  return rehearsalOnly(async () => {
    const response = await request(
      store,
      discovery,
      ['local:write'],
      {
        method: 'post',
        url: '/local/v1/commands',
        data: command,
      },
    );
    return response.data;
  });
}

export function clearRehearsalTransportSession() {
  scopedSession = null;
}
